import React, { useEffect, useState } from 'react'
import {
  ContentWrapper,
  ControlsWrapper,
  LoadingState,
  ProductTitle,
  ReviewList,
  ReviewsBody,
  ReviewsHeader,
  SubWrapper,
} from './ReviewsModal.styled'
import ReviewItem from '../../ReviewItem/ReviewItem'
import TypographyAtom from '../../../atoms/typography/typography'
import AddReviewButton from '../../AddReviewButton/AddReviewButton'
import ButtonAtom from '../../../atoms/buttons/buttons'
import useCustomer from '../../../../context/CustomerContext'
import { addReviewToProduct } from '../../../../utils/review/review'
import { useToast } from '../../../../context/ToastContext'
import FullPageModal from '../../Modal/FullPageModal'
import AddReviewForm from '../../Forms/AddReviewForm'
import getInitials from '../../../../helpers/getInitials'
import StockLabel from '../../StockLabel/StockLabel'
import { customerClient } from '../../../../utils/customer/customer'

function ReviewsModal({
  currentRef,
  open,
  reviews = [],
  variants = [],
  onClose,
  productTitle,
  productInfo,
  productId,
  productRating,
  totalReviews,
  totalCount,
  onLoadMore,
  isLoadMore,
  isReviewsLoading,
  isRefetching,
  retingFromNotification,
}) {
  const { getToken } = useCustomer()
  const [modalState, setModalState] = useState(retingFromNotification ? 'form' : 'list')
  const [showLeaveReviewButton, setShowLeaveReviewButton] = useState(false)
  const { showToast } = useToast()
  const accessToken = getToken()

  const variantsIdArray = variants.map(({ shopifyId }) => shopifyId)

  const handleLeaveReviewClick = () => {
    onLoadMore(0)
    setModalState('form')
    if (retingFromNotification) {
      currentRef.current.scrollTo({
        top: currentRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }

  const handleOnLoadMore = () => {
    onLoadMore()
  }

  const handleCloseModal = () => {
    setModalState('list')
    onClose()
  }

  const handleSubmit = async ({ name, email, title, body, rating }) => {
    try {
      const formData = new FormData()
      formData.set('id', productId)
      formData.set('name', name)
      formData.set('email', email)
      formData.set('title', title)
      formData.set('body', body)
      formData.set('rating', rating)

      await addReviewToProduct(formData)

      onLoadMore(1)
      showToast('Review has been sent', 'Thank you for your review!')
      setModalState('list')
    } catch (e) {
      showToast('Error sending review', 'Failed to submit a review')
    }
  }

  useEffect(() => {
    if (modalState === 'form' && currentRef.current) {
      currentRef.current.scrollTo({
        top: currentRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [modalState, retingFromNotification, currentRef])

  useEffect(() => {
    const fetchOrders = async () => {
      if (!accessToken) return

      const isAddingReviewAllowed = await customerClient.isProductPurchasedByCustomer({
        accessToken,
        variantsIdArray,
      })

      setShowLeaveReviewButton(isAddingReviewAllowed)
    }

    fetchOrders()
  }, [accessToken])

  return (
    <FullPageModal modalRef={currentRef} open={open} onClose={handleCloseModal}>
      <ReviewsHeader>
        <ContentWrapper>
          <ProductTitle>{productTitle}</ProductTitle>
          <div className="margin-md">{productInfo}</div>
          <ControlsWrapper>
            <SubWrapper>
              <StockLabel value={totalCount} inModal />
              <AddReviewButton
                reviews={totalReviews}
                rating={productRating}
                disabled
                inModalWindow
                className="add-review-button"
              />
            </SubWrapper>
            {showLeaveReviewButton && (
              <ButtonAtom
                buttonType="Primary Button"
                buttonCopy="Leave a review"
                onClick={handleLeaveReviewClick}
                isLoading={isRefetching}
              />
            )}
          </ControlsWrapper>
        </ContentWrapper>
      </ReviewsHeader>
      <ReviewsBody>
        <LoadingState loading={isRefetching} />
        {modalState === 'form' && (
          <ContentWrapper>
            <AddReviewForm
              onSubmit={handleSubmit}
              retingFromNotification={retingFromNotification}
            />
          </ContentWrapper>
        )}
        {modalState === 'list' && (
          <ContentWrapper>
            {reviews.length > 0 && (!accessToken || !showLeaveReviewButton) && (
              <div className="margin-customized">
                <TypographyAtom
                  type="Empty State"
                  weight="Regular"
                  copy={
                    (accessToken &&
                      !showLeaveReviewButton &&
                      'Please complete your purchase to leave a review') ||
                    'Please log in to leave a review about purchased products'
                  }
                />
              </div>
            )}

            {reviews.length > 0 ? (
              <>
                <ReviewList>
                  {reviews.map(({ id, title, body, updated_at, rating, reviewer }) => (
                    <li key={id}>
                      <ReviewItem
                        title={title}
                        body={body}
                        date={updated_at}
                        rating={rating}
                        reviewerInitials={reviewer.name ? getInitials(reviewer.name) : 'NA'}
                      />
                    </li>
                  ))}
                </ReviewList>
                {isLoadMore && (
                  <ButtonAtom
                    buttonType="Primary Button"
                    buttonCopy="Load more"
                    disabled={isReviewsLoading}
                    isLoading={isReviewsLoading}
                    onClick={handleOnLoadMore}
                  />
                )}
              </>
            ) : (
              <div className="margin-customized">
                <TypographyAtom
                  type="Empty State"
                  weight="Regular"
                  copy={
                    (accessToken &&
                      showLeaveReviewButton &&
                      'Be the first one to leave a review') ||
                    (accessToken &&
                      !showLeaveReviewButton &&
                      'Please complete your purchase to leave a review') ||
                    'Please log in to leave a review about purchased products'
                  }
                />
              </div>
            )}
          </ContentWrapper>
        )}
      </ReviewsBody>
    </FullPageModal>
  )
}

export default ReviewsModal
